<template>
    <div
        class="flex flex-col items-start flex-1 self-stretch sm:-mx-6 lg:-mx-8 -my-6 md:-my-8 pb-10"
    >
        <div
            class="flex flex-col items-center self-stretch px-4 sm:px-8 md:px-16 lg:px-24 pb-8 md:pb-16 bg-white dark:bg-slate-900 min-h-full"
        >
            <div class="flex flex-col items-start self-stretch">
                <!-- Header Navigation Area -->
                <div
                    class="flex flex-row justify-between items-center self-stretch mt-6 mb-4 md:my-12 gap-4"
                >
                    <!-- Back Link -->
                    <Link
                        href="/mynextcard"
                        class="flex items-center gap-2 rounded"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M8.78583 5.8681C9.11126 6.19354 9.11126 6.72118 8.78582 7.04661L6.66675 9.16567H15.0001C15.4603 9.16567 15.8334 9.53877 15.8334 9.99901C15.8334 10.4592 15.4603 10.8323 15.0001 10.8323H6.66674L8.78583 12.9514C9.11126 13.2769 9.11126 13.8045 8.78582 14.1299C8.46039 14.4554 7.93275 14.4554 7.60731 14.1299L4.6549 11.1775C4.00403 10.5266 4.00403 9.47137 4.65491 8.8205L7.60732 5.8681C7.93275 5.54266 8.46039 5.54266 8.78583 5.8681Z"
                                fill="#1E293B"
                            />
                        </svg>
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 block sm:hidden"
                        >
                            Back
                        </span>
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hidden sm:block"
                        >
                            Back to My Next Card
                        </span>
                    </Link>

                    <!-- Advertiser Disclosure -->
                    <a
                        href="#"
                        @click.prevent="setShowAffiliateModal(true)"
                        class="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 rounded hover:text-orange-400 ml-auto"
                        id="hs-next-card-affiliate-disclosure"
                    >
                        Advertiser disclosure
                    </a>
                </div>

                <!-- Loading State -->
                <div
                    v-if="loading"
                    class="relative flex self-stretch min-h-[60vh]"
                >
                    <LoadingState :loading="loading" />
                </div>

                <!-- Card Content Area -->
                <div
                    v-if="card && !loading"
                    class="flex flex-col-reverse lg:flex-row items-center self-stretch pb-12 md:pb-16 gap-12 md:gap-8"
                >
                    <!-- Left Content - Card Information -->
                    <div
                        class="flex flex-col items-start w-full lg:w-1/2 lg:pr-6"
                    >
                        <!-- Card Title -->
                        <h1
                            class="text-slate-800 dark:text-slate-100 text-3xl font-semibold leading-8 sm:leading-9 md:leading-10 self-stretch"
                            v-if="card"
                        >
                            {{ card.title }}
                        </h1>

                        <!-- Card Bonus Points Area -->
                        <div
                            class="prose dark:prose-invert flex flex-col items-start gap-2 self-stretch"
                            v-safe-html="card.rich_text"
                        ></div>
                        <!-- MOBILE: Botón 'Learn How to Apply' -->
                        <div
                            class="block lg:hidden w-full mt-5"
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'indirect'
                            "
                        >
                            <a
                                :href="applicationLinkWithMnc"
                                class="btn btn-orange w-full !ml-0 !text-md !mt-0"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Learn How to Apply
                                <icon
                                    icon="arrow-up-right"
                                    class="ml-2 w-5 h-5"
                                />
                            </a>
                            <a
                                :href="applicationLinkWithMnc"
                                v-else
                                class="text-sm sm:text-md font-semibold text-orange hover:text-orange-400 xl:mb-9 inline-flex items-center w-full justify-center"
                                target="_blank"
                            >
                                Learn How to Apply
                                <icon
                                    icon="arrow-up-right"
                                    class="ml-2 w-5 h-5"
                                />
                            </a>
                        </div>
                        <div v-if="card.rates_and_fees_link" class="mt-2">
                            <a
                                :href="card.rates_and_fees_link"
                                class="rates-fees-link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Rates & Fees
                            </a>
                        </div>

                        <!-- Apply Button -->
                        <div
                            class="card-link flex flex-col justify-between"
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'direct'
                            "
                        >
                            <a
                                :href="applicationLinkWithMnc"
                                class="flex justify-center items-center gap-2 px-4 py-2.5 btn-orange !mt-0"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Apply Now
                                <i class="fa-regular fa-lock ml-2"></i>
                            </a>
                            <p
                                class="text-center"
                                v-if="card.issuing_bank.link_button_visible"
                            >
                                <small>
                                    on {{ card.issuing_bank.name }}'s secure
                                    site
                                </small>
                            </p>
                        </div>
                        <div
                            class="card-link flex flex-col justify-between text-center hidden lg:flex mt-5"
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'indirect'
                            "
                        >
                            <a
                                :href="applicationLinkWithMnc"
                                class="btn btn-orange !ml-0 !text-md !mt-0"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Learn How to Apply
                                <icon
                                    icon="arrow-up-right"
                                    class="ml-2 w-5 h-5"
                                />
                            </a>
                            <a
                                :href="applicationLinkWithMnc"
                                v-else
                                class="text-sm sm:text-md font-semibold text-orange hover:text-orange-400 xl:mb-9 inline-flex items-center"
                                target="_blank"
                            >
                                Learn How to Apply
                                <icon
                                    icon="arrow-up-right"
                                    class="ml-2 w-5 h-5"
                                />
                            </a>
                        </div>
                    </div>

                    <!-- Right Content - Card Image and Stats -->
                    <div
                        class="flex flex-col items-center justify-center w-full lg:w-1/2"
                    >
                        <!-- Card Image -->
                        <div class="relative max-sm:mt-9">
                            <div
                                class="w-[280px] h-[176px] md:w-[364px] md:h-[228px] rounded-lg overflow-hidden relative z-10 bg-slate-100 dark:bg-slate-700"
                            >
                                <img
                                    v-if="card?.card_image"
                                    :src="getCardImageUrl(card.card_image)"
                                    alt="Card image"
                                    class="w-full h-full object-cover"
                                />
                                <div
                                    v-else
                                    class="w-full h-full flex items-center justify-center text-slate-400"
                                >
                                    Card Image
                                </div>
                            </div>

                            <div
                                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-x-[3.5] scale-y-[1.5] z-0 pointer-events-none"
                            >
                                <img
                                    src="/images/background/graphics.png"
                                    alt="Background pattern"
                                    class="w-96 h-96 opacity-50"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="flex flex-col sm:flex-row items-stretch self-stretch gap-6 sm:gap-0 min-h-16"
                >
                    <!-- Rewards Column -->
                    <div
                        v-if="pointsRange"
                        class="flex flex-col justify-center items-start gap-1 flex-1 py-0 px-6 pl-6 border-l-2 border-orange-500"
                    >
                        <p
                            class="text-slate-800 dark:text-slate-100 text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium leading-6 sm:leading-7 md:leading-8 lg:leading-9 break-words w-full pr-1"
                        >
                            {{ pointsRange }}
                        </p>
                        <p
                            class="text-slate-500 dark:text-slate-300 font-medium text-base leading-5 sm:leading-6"
                        >
                            Rewards
                        </p>
                    </div>

                    <!-- Annual Fee Column -->
                    <div
                        class="flex flex-col justify-center items-start gap-1 flex-1 py-0 px-6 pl-6 border-l-2 border-orange-500"
                    >
                        <p
                            class="text-slate-800 dark:text-slate-100 text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium leading-6 sm:leading-7 md:leading-8 lg:leading-9 break-words w-full pr-1"
                        >
                            ${{ card.annual_fee }}
                        </p>
                        <div class="flex items-end gap-1">
                            <p
                                class="text-slate-500 dark:text-slate-300 font-medium text-base leading-5 sm:leading-6"
                            >
                                Annual fee
                            </p>
                            <div class="flex h-4 pb-0.5 items-end gap-2.5">
                                <a
                                    v-if="card.rates_and_fees_link"
                                    :href="card.rates_and_fees_link"
                                    class="text-slate-500 dark:text-slate-300 font-normal text-xs leading-4"
                                    target="_blank"
                                >
                                    (Rates and fees)
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Welcome Offer Column -->
                    <div
                        v-if="
                            card.welcome_offer_data_source === 'rewardscc' &&
                            card.rewardscc_data
                        "
                        class="flex flex-col justify-center items-start gap-1 flex-1 py-0 px-6 pl-6 border-l-2 border-orange-500"
                    >
                        <div class="flex items-center w-full">
                            <p
                                class="text-slate-800 dark:text-slate-100 text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium leading-6 sm:leading-7 md:leading-8 lg:leading-9 break-words w-full pr-1"
                            >
                                {{
                                    $filters.formatThousand(
                                        card.rewardscc_data.signup_bonus_amount
                                    )
                                }}
                                <span>
                                    {{ card.rewardscc_data.signup_bonus_type }}
                                </span>
                            </p>
                        </div>
                        <p
                            class="text-slate-500 dark:text-slate-300 font-medium text-base leading-5 sm:leading-6"
                        >
                            Welcome offer
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Body Content -->
        <div
            v-if="card && !loading"
            class="flex flex-col items-center self-stretch pt-8 pb-20 md:px-24 md:pt-16 md:pb-24"
        >
            <div
                class="flex flex-col md:flex-row md:flex-wrap justify-center w-full gap-12 md:gap-16"
            >
                <!-- Left Column: Our Take -->
                <div
                    v-if="
                        card.our_take &&
                        card.our_take.replace(/<[^>]*>/g, '').trim().length > 0
                    "
                    class="our-take flex flex-col items-start self-stretch px-4 py-8 md:p-8 bg-slate-100 dark:bg-slate-700 rounded-lg flex-1"
                >
                    <div
                        class="flex flex-col justify-center items-start gap-1 self-stretch pb-6"
                    >
                        <h2
                            class="self-stretch text-slate-800 dark:text-slate-100 text-2xl sm:text-2xl md:text-3xl font-medium leading-7 md:leading-9"
                        >
                            Our take
                        </h2>
                    </div>
                    <!-- Desktop: Show our take content directly -->
                    <div
                        v-if="isLargeScreen"
                        v-safe-html="card.our_take"
                        class="prose prose-lg dark:prose-invert max-w-none flex flex-col gap-2 text-slate-800 dark:text-slate-100 text-base font-normal leading-6"
                    ></div>
                    <!-- Mobile: Wrap our take content in expandable section -->
                    <ExpandableSection
                        v-else
                        section-class="self-stretch flex-col justify-start items-start gap-4 flex transition-all ease-in-out duration-300 relative "
                        button-class="flex justify-center items-center text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight mt-4 focus:outline-none hover:text-slate-500 dark:hover:text-slate-300 active:text-slate-500 dark:active:text-slate-300 rounded"
                        maskGradientEndColor="rgba(241, 245, 249, 0.7)"
                    >
                        <template #content>
                            <div
                                v-safe-html="card.our_take"
                                class="prose prose-lg dark:prose-invert max-w-none text-slate-800 dark:text-slate-100 text-base font-normal leading-6 flex flex-col gap-2"
                            ></div>
                        </template>
                    </ExpandableSection>
                </div>

                <!-- Right Column: Rewards and Benefits -->
                <div
                    v-if="pointsTips.length || benefits.length"
                    class="flex flex-col p-6 md:p-8 pb-16 items-start gap-12 flex-1 rounded-lg bg-white dark:bg-slate-800 shadow-md dark:shadow-slate-900/50 mx-4"
                >
                    <!-- Rewards Section -->
                    <div
                        v-if="pointsTips.length"
                        class="flex flex-col items-start self-stretch"
                    >
                        <div
                            class="flex flex-col justify-center items-start gap-1 self-stretch pb-8"
                        >
                            <h2
                                class="self-stretch text-slate-800 dark:text-slate-100 text-2xl sm:text-2xl md:text-3xl font-medium leading-7 md:leading-9"
                            >
                                How to earn rewards
                            </h2>
                        </div>

                        <div
                            class="flex flex-col items-start gap-6 self-stretch"
                        >
                            <div
                                class="flex items-start gap-4 self-stretch"
                                v-for="(tip, index) in pointsTips"
                                :key="'tip-' + index"
                            >
                                <div
                                    class="flex w-12 py-0.5 flex-col justify-center items-center rounded bg-orange-50"
                                >
                                    <span
                                        class="self-stretch text-center text-base sm:text-lg md:text-xl font-semibold leading-6 sm:leading-7 md:leading-8 text-orange-600"
                                    >
                                        {{ tip.label }}
                                    </span>
                                </div>
                                <p
                                    class="flex-1 text-base font-normal leading-6 text-slate-800 dark:text-slate-100"
                                >
                                    {{ tip.value }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Benefits Section -->
                    <div
                        v-if="benefits.length"
                        class="flex flex-col items-start self-stretch"
                    >
                        <div
                            class="flex flex-col justify-center items-start gap-1 self-stretch pb-8"
                        >
                            <h2
                                class="self-stretch text-slate-800 dark:text-slate-100 text-2xl sm:text-2xl md:text-3xl font-medium leading-7 md:leading-9"
                            >
                                Benefits
                            </h2>
                        </div>

                        <div
                            class="flex flex-col items-start gap-6 self-stretch"
                        >
                            <div
                                v-for="(benefit, index) in benefits"
                                :key="'benefit-' + index"
                                class="flex items-start gap-4 self-stretch"
                            >
                                <BenefitIcon
                                    :category="benefit.category"
                                    :title="benefit.benefit_title"
                                    :description="benefit.benefit_description"
                                    svgClass="w-6 h-6 text-orange"
                                    containerClass="w-12 h-12 p-3"
                                    bgColorClass="bg-orange-50"
                                />
                                <p
                                    class="flex-1 text-slate-800 dark:text-slate-100 text-base font-normal leading-6"
                                >
                                    {{ benefit.benefit_title }}:
                                    {{ benefit.benefit_description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Transfer Partners Section -->
        <div
            v-if="
                transferPartnersGuideEnabled &&
                card &&
                card.program?.source_transfer_partners &&
                card.program.source_transfer_partners.length
            "
            class="flex flex-col items-center self-stretch px-4 sm:px-8 md:px-16 lg:px-24 pb-8 md:pb-24"
        >
            <div
                class="flex flex-col md:flex-row md:justify-between md:items-start self-stretch"
            >
                <div class="flex flex-col items-start gap-3 flex-1">
                    <h2
                        class="self-stretch text-slate-800 dark:text-slate-100 text-2xl sm:text-2xl md:text-3xl font-medium leading-7 md:leading-9"
                    >
                        Points transfer partners
                    </h2>
                    <p
                        class="text-slate-800 dark:text-slate-100 text-base font-normal leading-6 sm:leading-6"
                    >
                        {{ card.program.name }} lets you transfer points to
                        {{ partnerCountsSummary }} loyalty programs.
                    </p>
                </div>
                <img
                    v-if="card.program?.image"
                    :src="`${cdnUrl}/uploads/program_images/_small/${programImageBasename(card.program.image)}`"
                    class="w-[162px] h-12 mt-6 md:mt-0"
                    :alt="card.program.name"
                />
            </div>
            <div
                v-for="(partners, type) in groupedPartners"
                :key="type"
                class="w-full"
            >
                <div class="flex py-12 pb-6 items-center gap-3 self-stretch">
                    <!-- Transfer Icon -->
                    <h3
                        class="text-slate-800 dark:text-slate-100 text-base sm:text-lg font-medium leading-6 sm:leading-7 flex flex-row items-center gap-2"
                    >
                        <template v-if="type === 'flexible_currency'">
                            <Icon icon="transfer_points" class="w-5 h-5" />
                        </template>
                        <template v-else-if="type === 'hotel'">
                            <Icon icon="hotel" class="w-5 h-5" />
                        </template>
                        <template v-else-if="type === 'airline'">
                            <Icon icon="airplane" class="w-5 h-5" />
                        </template>
                        <template v-else-if="type === 'rail'">
                            <Icon icon="train" class="w-5 h-5" />
                        </template>
                        {{ humanizeType(type) }}
                    </h3>
                </div>
                <div
                    class="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6 md:gap-6 w-full"
                >
                    <div
                        v-for="partner in partners"
                        :key="partner.id"
                        class="flex flex-col items-start gap-2 col-span-1"
                    >
                        <div
                            class="flex h-28 px-4 flex-col justify-center items-center self-stretch rounded-lg bg-white dark:bg-slate-800"
                        >
                            <div
                                class="flex w-full max-w-40 h-16 justify-center items-center"
                            >
                                <img
                                    v-if="partner.destination_program?.image"
                                    :src="`${cdnUrl}/uploads/program_images/_small/${programImageBasename(partner.destination_program.image)}`"
                                    :alt="partner.destination_program.name"
                                    class="object-contain max-h-20 w-auto"
                                />
                                <div
                                    v-else
                                    class="flex items-center justify-center w-full h-full bg-slate-100 dark:bg-slate-700 rounded text-slate-400 text-xs text-center px-2"
                                >
                                    {{ partner.destination_program.name }}
                                </div>
                            </div>
                        </div>
                        <p
                            class="text-slate-500 dark:text-slate-300 text-center text-sm font-medium leading-5 self-stretch"
                        >
                            {{ partner.destination_program.name }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import { appendMncToUrl } from '@/utils/urlHelpers.js';
import { withCurrentPageTitle } from '@/utils/breadcrumbs.js';
import { useCardsStore } from '@/store/cards';
import { useNextCardStore } from '@/store/nextcard';
import { useAppStore } from '@/store/app';
import Icon from '@/components/ui/icon/icon.vue';
import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';
import ExpandableSection from '@/components/ui/expandable-section/ui-expandable-section.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';

const page = usePage();
const cardsStore = useCardsStore();
const nextCardStore = useNextCardStore();
const appStore = useAppStore();
const setShowAffiliateModal = appStore.setShowAffiliateModal;

const props = defineProps({
    id: {
        type: [String, Number],
        required: true,
    },
});

const card = computed(() => cardsStore.activeCard);
const applicationLinkWithMnc = computed(() => appendMncToUrl(card.value?.application_link));
const loading = ref(true);
const cardNotFound = ref(false);
const activeTab = ref('points');
const cdnUrl = ref(window.cdnUrl);
const transferPartners = ref([]);
const transferPartnersGuideEnabled = computed(
    () => appStore.userSettings?.features?.['transfer-partners-guide'] === true
);
const showTooltip = ref(false);
const isLargeScreen = ref(false);

// Detectar si la pantalla es grande (>1024px)
const checkScreenSize = () => {
    isLargeScreen.value = window.innerWidth > 1024;
};

// Comprobar tamaño de pantalla al montar el componente y al redimensionar
onMounted(() => {
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize);
});

const pointsTips = computed(() => {
    const rawData = card.value.points_tips;

    if (!rawData) {
        return [];
    }

    if (typeof rawData === 'string') {
        if (rawData.trim() === '') {
            return [];
        }
        try {
            const parsedData = JSON.parse(rawData);
            if (Array.isArray(parsedData)) {
                return parsedData;
            } else {
                return [];
            }
        } catch (error) {
            return [];
        }
    } else if (Array.isArray(rawData)) {
        return rawData;
    } else {
        return [];
    }
});

const benefits = computed(() => {
    const cardBenefits = card.value.card_benefits || [];
    return cardBenefits
        .filter((b) => b.current_version)
        .map((b) => ({
            ...b.current_version,
            is_trackable: b.is_trackable,
            enrollment_required: b.enrollment_required,
        }));
});

const pointsRange = computed(() => {
    const tips = pointsTips.value;
    if (!tips.length) return '';
    const nums = tips
        .map((t) => {
            const m = String(t.label).match(/([0-9]+(?:\.[0-9]+)?)/);
            return m ? parseFloat(m[1]) : NaN;
        })
        .filter((n) => !isNaN(n));
    if (!nums.length) return '';
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    return `${min}X-${max}X`;
});

const groupedPartners = computed(() => {
    const partners = card.value.program?.source_transfer_partners || [];
    if (!partners.length) return {};

    const grouped = {};
    partners.forEach((partner) => {
        const validTypes = ['airline', 'hotel', 'rail', 'flexible_currency'];
        if (!validTypes.includes(partner.destination_program?.type)) return;

        if (!grouped[partner.destination_program.type]) {
            grouped[partner.destination_program.type] = [];
        }
        grouped[partner.destination_program.type].push(partner);
    });
    return grouped;
});

const humanizeType = (type) => {
    switch (type) {
        case 'airline':
            return 'Airlines';
        case 'hotel':
            return 'Hotels';
        case 'rail':
            return 'Rail';
        case 'flexible_currency':
            return 'Flexible Currency';
        default:
            return 'Other';
    }
};

const airlinePartnersCount = computed(() => {
    const partners = card.value.program?.source_transfer_partners || [];
    return partners.filter(
        (partner) => partner.destination_program?.type === 'airline'
    ).length;
});

const hotelPartnersCount = computed(() => {
    const partners = card.value.program?.source_transfer_partners || [];
    return partners.filter(
        (partner) => partner.destination_program?.type === 'hotel'
    ).length;
});

const railPartnersCount = computed(() => {
    const partners = card.value.program?.source_transfer_partners || [];
    return partners.filter(
        (partner) => partner.destination_program?.type === 'rail'
    ).length;
});

// "16 airline and 3 hotel", or "16 airline, 3 hotel and 1 rail" when the card has rail partners.
const partnerCountsSummary = computed(() => {
    const counts = [
        `${airlinePartnersCount.value} airline`,
        `${hotelPartnersCount.value} hotel`,
    ];
    if (railPartnersCount.value > 0) {
        counts.push(`${railPartnersCount.value} rail`);
    }
    return `${counts.slice(0, -1).join(', ')} and ${counts.at(-1)}`;
});

const getCardImageUrl = (cardImage) => {
    if (!cardImage) return '';
    const cdnBase = cdnUrl.value;
    let path = cardImage.path.replace('rewardscc_card_images', 'card_images');
    return `${cdnBase}/${path}/_small/${cardImage.file_name}`;
};

const programImageBasename = (imagePath) => {
    if (!imagePath) return '';
    const parts = String(imagePath).split('/');
    return parts[parts.length - 1];
};

const getTransferPartnersData = async (programs) => {
    return [];
};

watch(card, async (newCard) => {
    if (newCard && newCard.title) {
        document.title = `${newCard.title} | My Next Card`;

        // Update breadcrumbs via Inertia page props
        nextTick(() => {
            const currentBreadcrumbs = page.props?.breadcrumbs || [];
            if (currentBreadcrumbs.length > 0) {
                // Update the last breadcrumb item (current page) with the actual card title
                page.props.breadcrumbs = withCurrentPageTitle(
                    currentBreadcrumbs,
                    newCard.title
                );
            }
        });
    }
});

async function loadCard(cardId) {
    if (!cardId) {
        cardNotFound.value = true;
        loading.value = false;
        return;
    }

    try {
        loading.value = true;
        cardNotFound.value = false;
        transferPartners.value = [];
        cardsStore.setActiveCard(null);
        await cardsStore.getCard(cardId);
        if (cardsStore.activeCard?.payment_programs) {
            transferPartners.value = await getTransferPartnersData(
                cardsStore.activeCard.payment_programs
            );
        }
    } catch (error) {
        console.error('Error loading card details:', error);
        cardNotFound.value = true;
    } finally {
        loading.value = false;
    }
}

watch(
    () => props.id,
    (newId) => loadCard(newId)
);

onMounted(() => loadCard(props.id));
</script>

<style scoped>
.our-take-content {
    color: var(--text-icons-primary, #1e293b);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
}
</style>

<style scoped>
.prose {
    color: #1e293b;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
}
:where(.dark) .prose {
    color: #f1f5f9;
}

.prose :deep(ul) {
    list-style-type: disc;
    padding-left: 1.5rem;
}

.prose :deep(li) {
    margin-bottom: 0.5rem;
}

.prose :deep(h3) {
    color: #fd421d;
    margin-top: 1rem;
    line-height: 1.5;
}

.prose :deep(p) {
    color: #1e293b;
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 0px;
}
:where(.dark) .prose :deep(p) {
    color: #f1f5f9;
}

.our-take :deep(a) {
    color: #fd421d;
    outline: 2px solid transparent;
    outline-offset: 2px;
}

.our-take :deep(a:hover),
.our-take :deep(a:active) {
    color: #fb923c;
}

.our-take :deep(a:focus) {
    outline: none;
    box-shadow: 0 0 0 2px rgb(203 213 225);
}

.btn-orange {
    background-color: #fd421d;
    color: white;
    font-weight: 500;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    cursor: pointer;
}

.btn-orange:hover {
    background-color: #e63a1a;
}
</style>
